/**
 * Offset paging over an OnlyFans list (vault media, list members, categories).
 *
 * OnlyFans counts `offset` in raw rows, hidden ones included, and returns the
 * next offset to ask for (0×20 → 21). Paging by rows shown re-reads a row or
 * two per page, so the pager follows `nextOffset` when given, drops repeats by
 * id, trusts `hasMore`, and stops on a page that adds nothing new so an
 * upstream that ignores the offset cannot loop on page one.
 */

export interface Page<T> {
  list: T[]
  hasMore?: boolean
  nextOffset?: number
}

export interface PagerOptions<T> {
  fetchPage: (offset: number) => Promise<Page<T>>
  idOf: (row: T) => string | number
}

export interface Pager<T> {
  readonly items: T[]
  readonly hasMore: boolean
  loadMore: () => Promise<void>
}

export function createPager<T>({ fetchPage, idOf }: PagerOptions<T>): Pager<T> {
  const items: T[] = []
  const seen = new Set<string | number>()
  let offset = 0
  let hasMore = true
  let inFlight: Promise<void> | null = null

  const fetchNext = async () => {
    const page = await fetchPage(offset)
    let added = 0
    for (const row of page.list) {
      const id = idOf(row)
      if (seen.has(id)) continue
      seen.add(id)
      items.push(row)
      added += 1
    }
    const reported = page.nextOffset
    offset = reported !== undefined && reported > offset ? reported : offset + page.list.length
    hasMore = (page.hasMore ?? page.list.length > 0) && added > 0
  }

  return {
    items,
    get hasMore() {
      return hasMore
    },
    loadMore() {
      if (!hasMore) return Promise.resolve()
      inFlight ??= fetchNext().finally(() => {
        inFlight = null
      })
      return inFlight
    },
  }
}
